import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle2, Calculator, BadgeCheck, HelpCircle, PhoneCall, BookOpen } from 'lucide-react';
import { Reveal, Words } from '../motion/Reveal';

const HERO_IMG = 'https://images.pexels.com/photos/36729883/pexels-photo-36729883.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600';
const WHY_IMG = 'https://images.unsplash.com/photo-1705313382153-ed294af4b0a7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';
const H2 = 'text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-tight';

export const RebuildHero = ({ c }) => (
  <section className="relative overflow-hidden min-h-[520px] md:min-h-[600px] flex items-center" data-testid="rebuild-hero">
    <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0c2418]/95 via-[#0c2418]/80 to-[#0c2418]/20" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
      <div className="max-w-2xl">
        <Reveal as="span" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-emerald-200 bg-white/10 border border-white/20 px-4 py-1.5 rounded-full mb-6">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />{c.eyebrow}
        </Reveal>
        <Words as="h1" parts={[[c.h1a, 'text-emerald-300'], [c.h1b]]} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mb-6 !text-white" data-testid="rebuild-heading" />
        <Reveal as="p" delay={400} className="text-lg md:text-xl text-slate-200 leading-relaxed mb-8">{c.sub}</Reveal>
        <Reveal delay={550} className="flex flex-wrap items-center gap-4">
          <Link to="/demander-un-pret-auto" className="group inline-flex items-center gap-2 bg-emerald-400 hover:bg-emerald-300 text-[#0c2418] font-bold text-base md:text-lg px-8 py-4 rounded-full shadow-[0_12px_35px_rgba(52,211,153,0.35)] hover:scale-[1.03] transition-[transform,background-color]" data-testid="rebuild-start-btn">
            {c.start}<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <a href="tel:5144183968" className="inline-flex items-center gap-2 font-semibold text-white hover:text-emerald-200" data-testid="rebuild-call"><PhoneCall className="w-4 h-4" />{c.call}</a>
        </Reveal>
      </div>
    </div>
  </section>
);

export const RebuildWhy = ({ c }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-5 gap-6 items-stretch" data-testid="rebuild-why">
    <Reveal className="lg:col-span-2 bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] rounded-[2rem] p-8 md:p-12 text-white flex flex-col justify-center">
      <Words parts={[[c.whyTitle]]} className="text-3xl md:text-4xl font-bold leading-tight mb-5 !text-white" />
      <p className="text-emerald-50/90 leading-relaxed mb-6">{c.whyText}</p>
      <ul className="space-y-3 mb-8">
        {c.whyPoints.map((p) => <li key={p} className="flex gap-3 items-start"><CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" /><span>{p}</span></li>)}
      </ul>
      <Link to="/verifier-admissibilite" className="self-start inline-flex items-center gap-2 bg-white text-[#1a5c3b] font-bold px-7 py-3.5 rounded-full hover:scale-[1.03] transition-transform" data-testid="rebuild-offer-btn">
        {c.offer}<ArrowRight className="w-4 h-4" />
      </Link>
    </Reveal>
    <Reveal delay={150} className="lg:col-span-3 rounded-[2rem] overflow-hidden min-h-[300px]">
      <img src={WHY_IMG} alt="" loading="lazy" className="w-full h-full object-cover" />
    </Reveal>
  </section>
);

export const RebuildSituations = ({ c }) => (
  <section className="bg-gradient-to-b from-[#eef6f0] to-[#f8faf7] py-16 md:py-24" data-testid="rebuild-situations">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <Words parts={[[c.sitTitle]]} className={`${H2} mb-4`} />
        <Reveal as="p" delay={150} className="text-lg text-slate-600">{c.sitSub}</Reveal>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {c.situations.map(([Icon, t, d], i) => (
          <Reveal key={t} delay={(i % 3) * 120} className="group bg-white rounded-3xl p-7 border border-emerald-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`rebuild-situation-${i + 1}`}>
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-[#1a5c3b] flex items-center justify-center mb-5 group-hover:bg-[#1a5c3b] group-hover:text-white group-hover:rotate-[-6deg] transition-[background-color,color,transform] duration-300"><Icon className="w-7 h-7" /></div>
            <h3 className="text-xl font-bold mb-2">{t}</h3>
            <p className="text-slate-600 leading-relaxed">{d}</p>
          </Reveal>
        ))}
      </div>
      <div className="text-center mt-12">
        <Link to="/demander-un-pret-auto" className="inline-flex items-center gap-2 bg-[#1a5c3b] hover:bg-[#165432] text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-[#1a5c3b]/25 hover:scale-[1.03] transition-[transform,background-color]" data-testid="rebuild-apply-btn">
          {c.apply}<ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  </section>
);

export const RebuildBlog = ({ c, en }) => {
  const [posts, setPosts] = useState([]);
  useEffect(() => { axios.get('/api/blog/posts').then((r) => setPosts((r.data || []).slice(0, 3))).catch(() => {}); }, []);
  if (!posts.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="rebuild-blog">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <Words parts={[[c.blogTitle]]} className={`${H2} mb-3`} />
          <p className="text-lg text-slate-600">{c.blogSub}</p>
        </div>
        <Link to="/blog" className="inline-flex items-center gap-2 font-semibold text-[#1a5c3b] hover:underline underline-offset-4" data-testid="rebuild-all-posts">{c.allPosts}<ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {posts.map((p, i) => (
          <Reveal key={p.id} delay={i * 120}>
            <Link to={`/blog/${p.slug}`} className="group flex flex-col h-full bg-[#f8faf7] rounded-3xl overflow-hidden border border-emerald-100 hover:shadow-xl transition-shadow" data-testid={`rebuild-post-${i + 1}`}>
              <div className="h-44 bg-gradient-to-br from-[#1a5c3b] to-emerald-500 overflow-hidden">
                {p.image_url ? <img src={p.image_url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : (
                  <div className="relative w-full h-full flex items-end p-6 bg-[radial-gradient(#ffffff22_1px,transparent_1px)] [background-size:18px_18px]">
                    <BookOpen className="absolute right-6 top-6 w-16 h-16 text-white/20 group-hover:scale-110 group-hover:rotate-[-8deg] transition-transform duration-500" />
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-white bg-white/15 border border-white/25 px-3 py-1 rounded-full">{(en ? {} : { financing: 'financement', credit: 'crédit', tips: 'conseils' })[p.category] || p.category || 'blog'}</span>
                  </div>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-bold mb-4 line-clamp-3">{en ? p.title_en : p.title_fr}</h3>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-[#1a5c3b]">{c.readMore}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export const RebuildTools = ({ c }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="rebuild-tools">
    <Words parts={[[c.toolsTitle]]} className={`${H2} mb-10 text-center`} />
    <div className="grid md:grid-cols-3 gap-6">
      {c.tools.map(([t, d, to, btn], i) => {
        const Icon = i === 0 ? Calculator : BadgeCheck;
        return (
          <Reveal key={t} delay={i * 120} className="bg-white border border-emerald-100 rounded-3xl p-8 shadow-sm flex flex-col">
            <div className="w-14 h-14 rounded-2xl bg-[#1a5c3b] text-white flex items-center justify-center mb-5"><Icon className="w-7 h-7" /></div>
            <h3 className="text-2xl font-bold mb-2">{t}</h3>
            <p className="text-slate-600 mb-6">{d}</p>
            <Link to={to} className="mt-auto inline-flex items-center gap-2 font-bold text-[#1a5c3b] hover:gap-3 transition-[gap]" data-testid={`rebuild-tool-${i + 1}`}>{btn}<ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
        );
      })}
      <Reveal delay={240} className="bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] rounded-3xl p-8 text-white flex flex-col">
        <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center mb-5"><HelpCircle className="w-7 h-7" /></div>
        <h3 className="text-2xl font-bold mb-2 !text-white">{c.faqTitle}</h3>
        <p className="text-emerald-100 mb-6">{c.faqText}</p>
        <Link to="/faq" className="mt-auto self-start inline-flex items-center gap-2 bg-white text-[#1a5c3b] font-bold px-6 py-3 rounded-full hover:scale-[1.03] transition-transform" data-testid="rebuild-faq-btn">{c.faqBtn}<ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
    </div>
  </section>
);
